// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import useHoldings from '@hooks/queries/registry/useHoldings';
import useIsPackageAvailableFrom from '@hooks/queries/settings/useIsPackageAvailableFrom';
import { useCallback, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Holding } from '@daml.js/utility-registry-holding-v0/lib/Utility/Registry/Holding/V0/Holding';
import { InstrumentIdentifier } from '@daml.js/utility-registry-holding-v0/lib/Utility/Registry/Holding/V0/Types';
import { TransferOffer } from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Holding/Transfer';
import FormDialog from '../../../components/Dialog/FormDialog';
import TextInput from '../../../components/Form/TextInput';
import { useParties } from '../../../contexts/PartiesContext';
import { useOfferTransfer } from '../../../hooks/mutations/registry/transfer/useOfferTransfer';
import TemplateContract from '../../../utils/TemplateContract';
import { filterHoldingsByInstrument } from '../../../utils/filterHoldingsByInstrument';
import { filterHoldingsByOwner } from '../../../utils/filterHoldingsByOwner';
import { getMinAmountCids } from '../../../utils/getMinAmountCids';
import { getValidateAmountMessage } from '../../../utils/getValidateAmountMessage';
import { useOfferTransferInstruction } from './useOfferTransferInstruction';

export type DialogProps = {
  open: boolean;
  onClose: () => void;
  holding?: TemplateContract<Holding>;
  disableDialog?: boolean;
};

export const OfferTransferDialog = ({ disableDialog, open, onClose, holding }: DialogProps) => {
  const [receiver, setReceiver] = useState<string>('');
  const [instrumentId, setInstrumentId] = useState<string>(holding?.payload.instrument.id || '');
  const [amount, setAmount] = useState<string>(holding?.payload.amount || '');
  const [senderLabel, setSenderLabel] = useState<string>(holding?.payload.label || '');
  const [registrar, setRegistrar] = useState<string>(holding?.payload.registrar || '');
  const [reference, setReference] = useState<string>('');
  const [amountValidationMsg, setAmountValidationMsg] = useState('');
  const holdings = useHoldings();
  const { operator, party } = useParties();
  const instrumentHoldings = filterHoldingsByInstrument({
    instrumentId,
    registrar,
    holdings: holdings.data || [],
  });
  const ownedHoldings = filterHoldingsByOwner({
    owner: party,
    holdings: instrumentHoldings,
  });
  const requiredCids = getMinAmountCids({
    requiredAmount: amount || '0',
    holdings: ownedHoldings || [],
  });
  const offerTransfer = useOfferTransfer();
  const enable2step = useIsPackageAvailableFrom(
    'utility-registry-app-v0',
    [operator, party],
    '0.3.0',
  );
  const offTransferInstruction = useOfferTransferInstruction();
  const onTransferTransferInstruction = async () => {
    await offTransferInstruction({
      party,
      receiver,
      amount,
      source: registrar,
      instrumentId,
      holdingCids: requiredCids,
    });
    onClose();
  };
  const resetState = useCallback(() => {
    setReceiver('');
    setInstrumentId('');
    setAmount('');
    setSenderLabel('');
    setRegistrar('');
    setReference('');
  }, [setReceiver, setInstrumentId, setAmount, setSenderLabel, setRegistrar, setReference]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleSubmit = useCallback(async () => {
    const payload: TransferOffer = {
      transfer: {
        operator,
        provider: party,
        sender: party,
        registrar,
        receiver,
        instrumentIdentifier: {
          source: registrar,
          id: instrumentId,
          scheme: 'RegistrarInternalScheme',
        } as InstrumentIdentifier,
        amount,
        reference,
        batch: { id: uuidv4(), size: '1', settlementFrom: null, settlementUntil: null },
      },
      senderLabel,
    };
    await offerTransfer({ argument: payload });
    handleClose();
  }, [
    senderLabel,
    amount,
    instrumentId,
    receiver,
    registrar,
    reference,
    offerTransfer,
    handleClose,
    operator,
    party,
  ]);

  const getTransferValidationMsg = getValidateAmountMessage('Transfer', holding?.payload.amount);

  const disableSubmit =
    !receiver || !instrumentId || !amount || !registrar || !!amountValidationMsg.length;
  return (
    <FormDialog
      disableDialog={disableDialog}
      open={open}
      title="Offer Transfer"
      onCancel={handleClose}
      onSubmit={enable2step.data ? onTransferTransferInstruction : handleSubmit}
      submitName="Offer"
      isSubmitDisabled={disableSubmit}
    >
      <TextInput label="Receiver" value={receiver} setValue={setReceiver} required />
      <TextInput
        label="Instrument"
        value={instrumentId}
        setValue={setInstrumentId}
        disabled={!!holding}
        required
      />
      <TextInput
        label="Amount"
        getValidationMessage={getTransferValidationMsg}
        setValidationMessage={setAmountValidationMsg}
        validationMessage={amountValidationMsg}
        value={amount}
        setValue={setAmount}
        required
        type="number"
      />
      <TextInput
        label="Label"
        value={senderLabel}
        setValue={setSenderLabel}
        disabled={!!holding}
        hidden
      />
      <TextInput
        label="Registrar"
        value={registrar}
        setValue={setRegistrar}
        required
        disabled={!!holding}
      />
      <TextInput label="Reference" value={reference} setValue={setReference} />
    </FormDialog>
  );
};
